import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { workspaceRepository } from "../lib/repository";
import type {
  AppSession,
  CreateProjectInput,
  CreateWorkItemInput,
  ProjectRole,
  UpdateProfileInput,
  WorkStatus,
  WorkspaceData,
} from "../types/domain";

interface WorkspaceContextValue {
  mode: "demo" | "supabase";
  session: AppSession | null;
  data: WorkspaceData | null;
  loading: boolean;
  error: string | null;
  refresh(): Promise<void>;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, displayName: string): Promise<string>;
  signOut(): Promise<void>;
  updateWorkItemStatus(itemId: string, status: WorkStatus, reason: string | null): Promise<void>;
  createProject(input: CreateProjectInput): Promise<void>;
  createWorkItem(input: CreateWorkItemInput): Promise<void>;
  addMemberByHandle(projectId: string, handle: string, role: ProjectRole): Promise<void>;
  updateMemberRole(projectId: string, userId: string, role: ProjectRole): Promise<void>;
  removeMember(projectId: string, userId: string): Promise<void>;
  updateProfile(input: UpdateProfileInput): Promise<void>;
  bootstrapEremosWorkspace(): Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<AppSession | null>(null);
  const [data, setData] = useState<WorkspaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const currentSession = await workspaceRepository.getSession();
      setSession(currentSession);
      if (currentSession) {
        setData(await workspaceRepository.loadWorkspace());
      } else {
        setData(null);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No se pudo cargar el workspace.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    return workspaceRepository.onAuthChange((nextSession) => {
      setSession(nextSession);
      if (nextSession) {
        void refresh();
      } else {
        setData(null);
        setLoading(false);
      }
    });
  }, [refresh]);

  const runAndRefresh = useCallback(
    async (operation: () => Promise<void>) => {
      setError(null);
      try {
        await operation();
        await refresh();
      } catch (caught) {
        const message =
          caught instanceof Error ? caught.message : "La operación no pudo completarse.";
        setError(message);
        throw caught;
      }
    },
    [refresh],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      mode: workspaceRepository.mode,
      session,
      data,
      loading,
      error,
      refresh,
      signIn: async (email, password) => {
        await workspaceRepository.signIn(email, password);
        await refresh();
      },
      signUp: async (email, password, displayName) => {
        const message = await workspaceRepository.signUp(email, password, displayName);
        await refresh();
        return message;
      },
      signOut: async () => {
        await workspaceRepository.signOut();
        setSession(null);
        setData(null);
      },
      updateWorkItemStatus: (itemId, status, reason) =>
        runAndRefresh(() => workspaceRepository.updateWorkItemStatus(itemId, status, reason)),
      createProject: (input) => runAndRefresh(() => workspaceRepository.createProject(input)),
      createWorkItem: (input) => runAndRefresh(() => workspaceRepository.createWorkItem(input)),
      addMemberByHandle: (projectId, handle, role) =>
        runAndRefresh(() => workspaceRepository.addMemberByHandle(projectId, handle, role)),
      updateMemberRole: (projectId, userId, role) =>
        runAndRefresh(() => workspaceRepository.updateMemberRole(projectId, userId, role)),
      removeMember: (projectId, userId) =>
        runAndRefresh(() => workspaceRepository.removeMember(projectId, userId)),
      updateProfile: (input) => runAndRefresh(() => workspaceRepository.updateProfile(input)),
      bootstrapEremosWorkspace: () =>
        runAndRefresh(() => workspaceRepository.bootstrapEremosWorkspace()),
    }),
    [data, error, loading, refresh, runAndRefresh, session],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error("useWorkspace debe usarse dentro de WorkspaceProvider.");
  return context;
}

export function roleForProject(
  data: WorkspaceData,
  projectId: string,
  userId: string,
): ProjectRole | null {
  return (
    data.members.find((member) => member.projectId === projectId && member.userId === userId)
      ?.role ?? null
  );
}
